/**
 * GuidedTour Component
 * AIDLC Administrative Backend
 *
 * Interactive product tour overlay that guides users through every module
 * of the AIDLC Admin portal. Uses a spotlight/cutout effect to highlight
 * target elements while dimming the rest of the interface.
 *
 * Features:
 * - Step-by-step guided tour covering all 8 modules + welcome + user info
 * - Dark overlay with animated cutout around the highlighted element
 * - Tooltip/popover with feature descriptions
 * - Next/Back/Skip navigation with step counter
 * - Smooth transitions between steps
 * - Keyboard navigation (Escape to skip, Arrow keys to navigate)
 * - Route navigation for steps that reference different pages
 * - LDAJA branded styling (blue accents, clean design)
 *
 * No external libraries required -- pure React + CSS.
 */

import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

// ============================================================
// Tour Step Definition
// ============================================================

export interface TourStep {
  /** CSS selector for the element to spotlight, or null for centered modal */
  target: string | null;
  /** Route path to navigate to before showing this step */
  route?: string;
  /** Title shown in the tooltip */
  title: string;
  /** Description text -- executive-friendly, no jargon */
  description: string;
  /** Position of the tooltip relative to the target */
  position: 'top' | 'bottom' | 'left' | 'right' | 'center';
}

// ============================================================
// Tour Steps -- All 12 covering every module
// ============================================================

export const TOUR_STEPS: TourStep[] = [
  // Step 0: Welcome (center modal, no target)
  {
    target: null,
    route: '/',
    title: 'Welcome to AIDLC Admin',
    description:
      'This is your command center for managing the AI Development Lifecycle. ' +
      'From here, you can monitor your team of 14 AI agents, assign tasks through natural conversation, ' +
      'track project progress, and ensure quality governance across every initiative. ' +
      'Let us walk you through each module.',
    position: 'center',
  },

  // Step 1: Sidebar Navigation
  {
    target: 'aside',
    route: '/',
    title: 'Navigation Sidebar',
    description:
      'This sidebar is your primary navigation. It provides quick access to all 8 modules ' +
      'of the AIDLC system. Each module is designed for a specific operational function -- ' +
      'from monitoring agents to tracking project lifecycles. The blue highlight shows ' +
      'which module you are currently viewing.',
    position: 'right',
  },

  // Step 2: Agent Dashboard
  {
    target: 'main',
    route: '/',
    title: 'Agent Dashboard',
    description:
      'This is your home screen -- a real-time overview of all AI agents in the system. ' +
      'The summary bar at the top shows how many agents are working, available, waiting, or away, ' +
      'plus total token usage across the team. You can search for specific agents by name, role, ' +
      'or expertise, and filter by status to focus on what matters right now.',
    position: 'top',
  },

  // Step 3: Governance
  {
    target: 'main',
    route: '/governance',
    title: 'Governance & Compliance',
    description:
      'This module lets you define and enforce the rules that govern how your AI agents operate. ' +
      'There are four types of rules: Quality Gates ensure work meets standards before advancing. ' +
      'SOPs define standard procedures agents must follow. Approval Workflows require sign-off ' +
      'before critical actions. Security Policies enforce data protection and access controls. ' +
      'Each rule can be toggled on or off instantly.',
    position: 'top',
  },

  // Step 4: Control Room
  {
    target: 'main',
    route: '/control',
    title: 'Control Room',
    description:
      'The operational command center for real-time agent management. See all agents organized ' +
      'by their current status -- working, available, waiting, or away. You can change any ' +
      'agent\'s status with a single click. The Prompt Queue tab shows all queued tasks ' +
      'with their priority, status, and assigned agent, allowing you to start, complete, ' +
      'or mark prompts as failed. Data refreshes automatically every 15 seconds.',
    position: 'top',
  },

  // Step 5: AIDLC Lifecycle
  {
    target: 'main',
    route: '/lifecycle',
    title: 'AIDLC Lifecycle',
    description:
      'Every project moves through 7 defined stages: Intent, Requirements, Planning, Estimating, ' +
      'Implementing, Delivering, and Complete. The pipeline visualization at the top shows how many ' +
      'projects are in each stage. Click any project to expand its details -- tasks, decisions, risks, ' +
      'and a full state transition history. The "Advance" button lets you move a project to ' +
      'its next stage with documented notes explaining why.',
    position: 'top',
  },

  // Step 6: Chat Interface -- THE CROWN JEWEL
  {
    target: 'main',
    route: '/chat',
    title: 'Chat Interface -- Your Primary Workflow',
    description:
      'This is the most important module in the system. It is a three-panel workspace: ' +
      'the left panel lists your conversations with each agent; the center panel shows the ' +
      'message thread where you communicate directly with any agent; and the right panel ' +
      'displays the Prompt Queue showing all queued tasks and their status. ' +
      'The key feature is the green "Go Do It" button -- click it to convert any conversation ' +
      'into an actionable task. It auto-populates with conversation context, lets you set priority ' +
      'and assign a project, then queues it for execution. This is how you direct your AI team.',
    position: 'top',
  },

  // Step 7: Audit & History
  {
    target: 'main',
    route: '/audit',
    title: 'Audit & History',
    description:
      'Complete accountability for every action in the system. The Audit Logs tab provides ' +
      'a filterable table of all events -- tasks assigned, tasks completed, status changes, ' +
      'code reviews, governance triggers, and authentication events. Filter by agent, action type, ' +
      'or date range. The Sessions tab shows deployment records including which agents were ' +
      'spawned, how many tokens they consumed, and what tasks they completed. ' +
      'Token usage is tracked per-agent for full cost visibility.',
    position: 'top',
  },

  // Step 8: Templates
  {
    target: 'main',
    route: '/templates',
    title: 'Templates',
    description:
      'Build reusable workflow templates for tasks you run frequently. Each template defines ' +
      'a sequence of steps, the agents involved, an estimated duration, and optional quality gates ' +
      'between steps. Templates are organized by category and can be searched. ' +
      'Instead of explaining the same task every time, create a template once ' +
      'and reuse it whenever that workflow is needed.',
    position: 'top',
  },

  // Step 9: Settings
  {
    target: 'main',
    route: '/settings',
    title: 'Settings & System Health',
    description:
      'Your system administration view. Check the backend API health status and uptime. ' +
      'View your authentication details and role-based access configuration powered by ' +
      'Microsoft Entra ID. See all registered agents and their current status at a glance. ' +
      'Review the Azure infrastructure resources -- Cosmos DB, App Service, Key Vault, ' +
      'Application Insights -- and all API endpoints available in the system.',
    position: 'top',
  },

  // Step 10: User Info in Sidebar
  {
    target: 'aside .border-t',
    route: '/',
    title: 'Your Account',
    description:
      'At the bottom of the sidebar, you can always see who is currently logged in, ' +
      'your email address, and your assigned role (Admin, Operator, or Viewer). ' +
      'Your role determines what actions you can perform -- Admins have full control, ' +
      'Operators can manage agents and prompts, and Viewers have read-only access. ' +
      'Use "Sign Out" to securely end your session.',
    position: 'right',
  },

  // Step 11: Finish
  {
    target: null,
    route: '/',
    title: 'You Are Ready',
    description:
      'You have seen every module in the AIDLC Admin portal. Here is the quick summary: ' +
      'Start on the Agent Dashboard to see your team. Use the Chat Interface to talk to agents and ' +
      'assign tasks with "Go Do It." Track progress in AIDLC Lifecycle. Enforce standards through ' +
      'Governance. Monitor operations in the Control Room. Review history in Audit. Save time with Templates. ' +
      'Check system health in Settings. You can retake this tour anytime by clicking ' +
      '"Take Tour" in the header bar.',
    position: 'center',
  },
];

// ============================================================
// Component Props
// ============================================================

interface GuidedTourProps {
  isActive: boolean;
  currentStep: number;
  totalSteps: number;
  onNext: () => void;
  onPrev: () => void;
  onSkip: () => void;
  onGoToStep: (step: number) => void;
  onComplete: () => void;
}

// ============================================================
// Spotlight Rect -- Position and size of the cutout
// ============================================================

interface SpotlightRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

// ============================================================
// GuidedTour Component
// ============================================================

export function GuidedTour({
  isActive,
  currentStep,
  totalSteps,
  onNext,
  onPrev,
  onSkip,
  onGoToStep,
  onComplete,
}: GuidedTourProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [spotlight, setSpotlight] = useState<SpotlightRect | null>(null);
  const [tooltipStyle, setTooltipStyle] = useState<React.CSSProperties>({});
  const [isTransitioning, setIsTransitioning] = useState(false);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const resizeTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const step = TOUR_STEPS[currentStep];
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === totalSteps - 1;

  // ----------------------------------------------------------
  // Navigate to the correct route for the current step
  // ----------------------------------------------------------
  useEffect(() => {
    if (!isActive || !step?.route) return;
    if (location.pathname !== step.route) {
      setIsTransitioning(true);
      navigate(step.route);
      // Allow time for the route transition and content render
      const timer = setTimeout(() => setIsTransitioning(false), 400);
      return () => clearTimeout(timer);
    }
  }, [isActive, currentStep, step?.route, location.pathname, navigate]);

  // ----------------------------------------------------------
  // Calculate spotlight position and tooltip placement
  // ----------------------------------------------------------
  const calculatePositions = useCallback(() => {
    if (!isActive || !step) return;

    if (!step.target || step.position === 'center') {
      setSpotlight(null);
      setTooltipStyle({
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
      });
      return;
    }

    const targetEl = document.querySelector(step.target);
    if (!targetEl) {
      // Fallback to center if target not found
      setSpotlight(null);
      setTooltipStyle({
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
      });
      return;
    }

    const rect = targetEl.getBoundingClientRect();
    const padding = 8;
    const spotRect: SpotlightRect = {
      top: rect.top - padding,
      left: rect.left - padding,
      width: rect.width + padding * 2,
      height: rect.height + padding * 2,
    };
    setSpotlight(spotRect);

    // Calculate tooltip position based on step.position
    const tooltipWidth = 420;
    const tooltipGap = 16;
    const style: React.CSSProperties = { position: 'fixed' };

    switch (step.position) {
      case 'right':
        style.top = Math.max(80, spotRect.top + spotRect.height / 2);
        style.left = spotRect.left + spotRect.width + tooltipGap;
        style.transform = 'translateY(-50%)';
        // Prevent going off screen right
        if ((style.left as number) + tooltipWidth > window.innerWidth - 20) {
          style.left = window.innerWidth - tooltipWidth - 20;
        }
        break;
      case 'left':
        style.top = spotRect.top + spotRect.height / 2;
        style.right = window.innerWidth - spotRect.left + tooltipGap;
        style.transform = 'translateY(-50%)';
        break;
      case 'bottom':
        style.top = spotRect.top + spotRect.height + tooltipGap;
        style.left = Math.max(240, spotRect.left + spotRect.width / 2);
        style.transform = 'translateX(-50%)';
        break;
      case 'top':
      default:
        // For main content steps, position tooltip at center-top of viewport
        style.top = 80;
        style.left = '50%';
        style.transform = 'translateX(-50%)';
        // Ensure tooltip does not sit inside sidebar area
        break;
    }

    setTooltipStyle(style);
  }, [isActive, step, currentStep]);

  // Recalculate on step change or resize
  useEffect(() => {
    if (!isActive) return;

    // Delay to allow route change and render
    const timer = setTimeout(calculatePositions, 150);

    const handleResize = () => {
      clearTimeout(resizeTimerRef.current);
      resizeTimerRef.current = setTimeout(calculatePositions, 100);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      clearTimeout(timer);
      clearTimeout(resizeTimerRef.current);
      window.removeEventListener('resize', handleResize);
    };
  }, [isActive, currentStep, calculatePositions, isTransitioning]);

  // ----------------------------------------------------------
  // Keyboard navigation
  // ----------------------------------------------------------
  useEffect(() => {
    if (!isActive) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'Escape':
          onSkip();
          break;
        case 'ArrowRight':
        case 'Enter':
          e.preventDefault();
          if (isLastStep) {
            onComplete();
          } else {
            onNext();
          }
          break;
        case 'ArrowLeft':
          e.preventDefault();
          if (!isFirstStep) onPrev();
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isActive, isFirstStep, isLastStep, onNext, onPrev, onSkip, onComplete]);

  // ----------------------------------------------------------
  // Don't render if tour is not active
  // ----------------------------------------------------------
  if (!isActive || !step) return null;

  // ----------------------------------------------------------
  // Build the overlay with cutout using CSS box-shadow
  // ----------------------------------------------------------
  const overlayStyle: React.CSSProperties = spotlight
    ? {
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        zIndex: 9998,
        pointerEvents: 'none',
      }
    : {
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        zIndex: 9998,
        backgroundColor: 'rgba(0, 0, 0, 0.65)',
        pointerEvents: 'none',
      };

  // The cutout element -- a transparent box with a massive box-shadow
  const cutoutStyle: React.CSSProperties | null = spotlight
    ? {
        position: 'fixed',
        top: spotlight.top,
        left: spotlight.left,
        width: spotlight.width,
        height: spotlight.height,
        borderRadius: '8px',
        boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.65)',
        zIndex: 9998,
        pointerEvents: 'none',
        transition: 'top 0.3s ease, left 0.3s ease, width 0.3s ease, height 0.3s ease',
      }
    : null;

  return (
    <>
      {/* Overlay */}
      <div style={overlayStyle} aria-hidden="true" />

      {/* Cutout spotlight */}
      {cutoutStyle && <div style={cutoutStyle} aria-hidden="true" />}

      {/* Click blocker -- sits over everything to prevent interaction during tour */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          zIndex: 9999,
          cursor: 'default',
        }}
        onClick={(e) => e.stopPropagation()}
        aria-hidden="true"
      />

      {/* Tooltip */}
      <div
        ref={tooltipRef}
        style={{
          ...tooltipStyle,
          zIndex: 10000,
          maxWidth: step.position === 'center' ? '520px' : '420px',
          width: step.position === 'center' ? '520px' : '420px',
        }}
        role="dialog"
        aria-label={`Tour step ${currentStep + 1} of ${totalSteps}: ${step.title}`}
        aria-modal="true"
        className="tour-tooltip"
      >
        <div
          style={{
            backgroundColor: '#FFFFFF',
            borderRadius: '12px',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(37, 99, 235, 0.2)',
            overflow: 'hidden',
            animation: 'tourFadeIn 0.25s ease-out',
          }}
        >
          {/* Blue accent bar at top */}
          <div
            style={{
              height: '4px',
              background: 'linear-gradient(90deg, #2563EB, #7DD3FC)',
            }}
          />

          {/* Content */}
          <div style={{ padding: '24px 28px 20px' }}>
            {/* Step counter */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '12px',
              }}
            >
              <span
                style={{
                  fontSize: '11px',
                  fontWeight: 600,
                  color: '#2563EB',
                  letterSpacing: '0.05em',
                  textTransform: 'uppercase',
                }}
              >
                Step {currentStep + 1} of {totalSteps}
              </span>
              <button
                onClick={onSkip}
                style={{
                  fontSize: '12px',
                  color: '#9CA3AF',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  transition: 'color 0.15s',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.color = '#4B5563')}
                onMouseLeave={(e) => (e.currentTarget.style.color = '#9CA3AF')}
                aria-label="Skip tour"
              >
                Skip Tour
              </button>
            </div>

            {/* Title */}
            <h3
              style={{
                fontSize: '18px',
                fontWeight: 700,
                color: '#000000',
                margin: '0 0 10px 0',
                lineHeight: 1.3,
              }}
            >
              {step.title}
            </h3>

            {/* Description */}
            <p
              style={{
                fontSize: '14px',
                lineHeight: 1.65,
                color: '#4B5563',
                margin: '0 0 20px 0',
              }}
            >
              {step.description}
            </p>

            {/* Progress dots */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                marginBottom: '16px',
                justifyContent: 'center',
              }}
            >
              {Array.from({ length: totalSteps }).map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => onGoToStep(idx)}
                  style={{
                    width: idx === currentStep ? '20px' : '6px',
                    height: '6px',
                    borderRadius: '3px',
                    backgroundColor: idx === currentStep ? '#2563EB' : idx < currentStep ? '#7DD3FC' : '#E5E7EB',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 0,
                    transition: 'all 0.2s ease',
                  }}
                  aria-label={`Go to step ${idx + 1}`}
                />
              ))}
            </div>

            {/* Navigation buttons */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '8px',
              }}
            >
              {/* Back button */}
              <button
                onClick={onPrev}
                disabled={isFirstStep}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '8px 16px',
                  fontSize: '13px',
                  fontWeight: 500,
                  color: isFirstStep ? '#D1D5DB' : '#4B5563',
                  backgroundColor: 'transparent',
                  border: '1px solid',
                  borderColor: isFirstStep ? '#E5E7EB' : '#D1D5DB',
                  borderRadius: '8px',
                  cursor: isFirstStep ? 'default' : 'pointer',
                  transition: 'all 0.15s',
                }}
                onMouseEnter={(e) => {
                  if (!isFirstStep) {
                    e.currentTarget.style.borderColor = '#9CA3AF';
                    e.currentTarget.style.backgroundColor = '#F9FAFB';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = isFirstStep ? '#E5E7EB' : '#D1D5DB';
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
                aria-label="Previous step"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                  <path d="M19 12H5M12 19l-7-7 7-7" />
                </svg>
                Back
              </button>

              {/* Keyboard hint */}
              <span
                style={{
                  fontSize: '10px',
                  color: '#9CA3AF',
                }}
              >
                Use arrow keys to navigate
              </span>

              {/* Next / Finish button */}
              <button
                onClick={isLastStep ? onComplete : onNext}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '8px 20px',
                  fontSize: '13px',
                  fontWeight: 600,
                  color: '#FFFFFF',
                  backgroundColor: isLastStep ? '#16A34A' : '#2563EB',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'background-color 0.15s',
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.12)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = isLastStep ? '#15803D' : '#1D4ED8';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = isLastStep ? '#16A34A' : '#2563EB';
                }}
                aria-label={isLastStep ? 'Finish tour' : 'Next step'}
              >
                {isLastStep ? 'Get Started' : 'Next'}
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                  <path d={isLastStep ? 'M5 12l5 5L20 7' : 'M5 12h14M12 5l7 7-7 7'} />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Keyframes for fade-in animation */}
      <style>{`
        @keyframes tourFadeIn {
          from {
            opacity: 0;
            transform: translateY(8px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </>
  );
}
